import { ResponsiveContainer, AreaChart, Area, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Cell } from "recharts";

const TOOLTIP_STYLE = {
  backgroundColor: "#0B1035",
  border: "1px solid rgba(255,255,255,0.15)",
  borderRadius: "8px",
  fontSize: "12px",
  color: "#fff",
};
const AREA_MARGIN = { top: 5, right: 10, left: -25, bottom: 0 };
const BAR_MARGIN = { top: 5, right: 20, left: 10, bottom: 0 };
const AXIS_TICK = { fill: "#64748b", fontSize: 10 };
const CATEGORY_TICK = { fill: "#94a3b8", fontSize: 11 };
const LABEL_STYLE = { color: "#94a3b8" };
const CURSOR_FILL = { fill: "rgba(255,255,255,0.04)" };
const BAR_RADIUS = [0, 4, 4, 0];
const BAR_COLORS = ["#22d3ee", "#8b5cf6", "#a78bfa", "#67e8f9", "#c4b5fd", "#38bdf8"];

const buildTrend = (inquiries, days = 30) => {
  const buckets = {};
  for (let d = days - 1; d >= 0; d--) {
    const date = new Date(Date.now() - d * 86400000);
    buckets[date.toISOString().slice(0, 10)] = 0;
  }
  inquiries.forEach((i) => {
    const key = new Date(i.created_at).toISOString().slice(0, 10);
    if (key in buckets) buckets[key] += 1;
  });
  return Object.entries(buckets).map(([date, count]) => ({
    label: new Date(date + "T12:00:00").toLocaleDateString(undefined, { month: "short", day: "numeric" }),
    count,
  }));
};

const buildTopServices = (inquiries, top = 6) => {
  const counts = {};
  inquiries.forEach((i) => (i.services || []).forEach((s) => { counts[s] = (counts[s] || 0) + 1; }));
  return Object.entries(counts)
    .sort((a, b) => b[1] - a[1])
    .slice(0, top)
    .map(([name, count]) => ({ name: name.length > 34 ? name.slice(0, 32) + "…" : name, count }));
};

const buildTiles = (inquiries) => {
  const thisWeek = inquiries.filter((i) => new Date(i.created_at) > new Date(Date.now() - 7 * 86400000)).length;
  const hot = inquiries.filter((i) => i.lead_tier === "hot").length;
  const avgScore = inquiries.length
    ? Math.round(inquiries.reduce((sum, i) => sum + (i.lead_score || 0), 0) / inquiries.length)
    : 0;
  return [
    { label: "Total Inquiries", value: inquiries.length },
    { label: "Last 7 Days", value: thisWeek },
    { label: "Hot Leads", value: hot },
    { label: "Avg Lead Score", value: avgScore },
  ];
};

const StatTiles = ({ tiles }) => (
  <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
    {tiles.map((t) => (
      <div key={t.label} data-testid={`analytics-tile-${t.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
        className="bg-[#0B1035] border border-white/10 p-5">
        <p className="font-heading text-3xl font-medium text-white">{t.value}</p>
        <p className="mt-1 text-xs text-slate-400 uppercase tracking-widest">{t.label}</p>
      </div>
    ))}
  </div>
);

const TrendChart = ({ trend }) => (
  <div data-testid="trend-chart" className="bg-[#0B1035] border border-white/10 p-5">
    <h3 className="text-sm font-semibold text-slate-300 uppercase tracking-widest">Inquiries — Last 30 Days</h3>
    <div className="mt-4 h-56">
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={trend} margin={AREA_MARGIN}>
          <defs>
            <linearGradient id="trendFill" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#22d3ee" stopOpacity={0.35} />
              <stop offset="100%" stopColor="#22d3ee" stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid stroke="rgba(255,255,255,0.06)" vertical={false} />
          <XAxis dataKey="label" tick={AXIS_TICK} interval={6} axisLine={false} tickLine={false} />
          <YAxis allowDecimals={false} tick={AXIS_TICK} axisLine={false} tickLine={false} />
          <Tooltip contentStyle={TOOLTIP_STYLE} labelStyle={LABEL_STYLE} />
          <Area type="monotone" dataKey="count" name="Inquiries" stroke="#22d3ee" strokeWidth={2} fill="url(#trendFill)" />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  </div>
);

const ServicesChart = ({ services }) => (
  <div data-testid="services-chart" className="bg-[#0B1035] border border-white/10 p-5">
    <h3 className="text-sm font-semibold text-slate-300 uppercase tracking-widest">Top Requested Services</h3>
    {services.length === 0 ? (
      <p className="mt-8 text-sm text-slate-500">No service selections yet.</p>
    ) : (
      <div className="mt-4 h-56">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={services} layout="vertical" margin={BAR_MARGIN}>
            <CartesianGrid stroke="rgba(255,255,255,0.06)" horizontal={false} />
            <XAxis type="number" allowDecimals={false} tick={AXIS_TICK} axisLine={false} tickLine={false} />
            <YAxis type="category" dataKey="name" width={190} tick={CATEGORY_TICK} axisLine={false} tickLine={false} />
            <Tooltip contentStyle={TOOLTIP_STYLE} cursor={CURSOR_FILL} />
            <Bar dataKey="count" name="Requests" radius={BAR_RADIUS} barSize={18}>
              {services.map((s, i) => <Cell key={s.name} fill={BAR_COLORS[i % BAR_COLORS.length]} />)}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    )}
  </div>
);

export const AnalyticsPanel = ({ inquiries }) => (
  <div data-testid="analytics-panel" className="mt-8 space-y-4">
    <StatTiles tiles={buildTiles(inquiries)} />
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      <TrendChart trend={buildTrend(inquiries)} />
      <ServicesChart services={buildTopServices(inquiries)} />
    </div>
  </div>
);
